// TanStack Query hooks for the documents module (FR-2.x). Query keys: ['w', wsId, 'documents', ...].
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateDocumentLinksResponse,
  DocumentDetail,
  DocumentDownloadResponse,
  DocumentDuplicatesResponse,
  DocumentList,
  DocumentCategory,
  DocumentSort,
  OcrStatus,
  DocumentsSummary,
  DocumentTagsResponse,
  DocumentTextResponse,
  EntityType,
  KeyDocumentChecklist,
  LinkableRecordsResponse,
  LinkedDocumentsResponse,
  UpdateDocumentRequest,
} from '@seventide/types';
import { useOptionalWorkspace } from '../../hooks/useWorkspace';
import { api, wsPath } from '../../lib/api';
import { qk } from '../../lib/queryClient';

/** Vault list filters (mirrors `DocumentsQuery` in @seventide/types). */
export interface DocumentsQuery {
  q?: string;
  category?: DocumentCategory[];
  tag?: string[];
  ocrStatus?: OcrStatus;
  from?: string;
  to?: string;
  sort?: DocumentSort;
  deleted?: boolean;
  limit?: number;
  offset?: number;
}

export const docKeys = {
  all: (wsId: string) => qk.ws(wsId, 'documents'),
  list: (wsId: string, q: DocumentsQuery) => qk.ws(wsId, 'documents', 'list', q),
  detail: (wsId: string, id: string) => qk.ws(wsId, 'documents', 'detail', id),
  text: (wsId: string, id: string) => qk.ws(wsId, 'documents', 'text', id),
  links: (wsId: string, entityType: string, entityId: string) => qk.ws(wsId, 'documents', 'links', entityType, entityId),
  checklist: (wsId: string) => qk.ws(wsId, 'documents', 'checklist'),
  summary: (wsId: string) => qk.ws(wsId, 'documents', 'summary'),
  tags: (wsId: string) => qk.ws(wsId, 'documents', 'tags'),
  linkable: (wsId: string, entityType: string) => qk.ws(wsId, 'documents', 'linkable', entityType),
  view: (wsId: string, id: string, disposition: string) => qk.ws(wsId, 'documents', 'view', id, disposition),
};

function useWsId(): string {
  return useOptionalWorkspace()?.wsId ?? '';
}

export function useDocuments(q: DocumentsQuery, opts: { enabled?: boolean } = {}) {
  const wsId = useWsId();
  return useQuery({
    queryKey: docKeys.list(wsId, q),
    queryFn: ({ signal }) =>
      api.get<DocumentList>(wsPath(wsId, 'documents'), {
        signal,
        query: {
          q: q.q,
          category: q.category,
          tag: q.tag,
          ocrStatus: q.ocrStatus,
          from: q.from,
          to: q.to,
          sort: q.sort,
          deleted: q.deleted ? 'true' : undefined,
          limit: q.limit,
          offset: q.offset,
        },
      }),
    enabled: Boolean(wsId) && opts.enabled !== false,
    placeholderData: keepPreviousData,
  });
}

/** Polls while text extraction is pending so the page updates by itself. */
export function useDocument(id: string | undefined) {
  const wsId = useWsId();
  return useQuery({
    queryKey: docKeys.detail(wsId, id ?? ''),
    queryFn: () => api.get<DocumentDetail>(wsPath(wsId, 'documents', id ?? '')),
    enabled: Boolean(wsId && id),
    refetchInterval: (query) => {
      const s = query.state.data?.ocrStatus;
      return s === 'pending' || s === 'processing' ? 4000 : false;
    },
  });
}

export function useDocumentText(id: string, enabled: boolean) {
  const wsId = useWsId();
  return useQuery({
    queryKey: docKeys.text(wsId, id),
    queryFn: () => api.get<DocumentTextResponse>(wsPath(wsId, 'documents', id, 'text')),
    enabled: Boolean(wsId && id) && enabled,
  });
}

export function useChecklist(enabled = true) {
  const wsId = useWsId();
  return useQuery({
    queryKey: docKeys.checklist(wsId),
    queryFn: () => api.get<KeyDocumentChecklist>(wsPath(wsId, 'documents', 'checklist')),
    enabled: Boolean(wsId) && enabled,
  });
}

export function useDocumentsSummary() {
  const wsId = useWsId();
  return useQuery({
    queryKey: docKeys.summary(wsId),
    queryFn: () => api.get<DocumentsSummary>(wsPath(wsId, 'documents', 'summary')),
    enabled: Boolean(wsId),
  });
}

export function useDocumentTags(enabled = true) {
  const wsId = useWsId();
  return useQuery({
    queryKey: docKeys.tags(wsId),
    queryFn: () => api.get<DocumentTagsResponse>(wsPath(wsId, 'documents', 'tags')),
    enabled: Boolean(wsId) && enabled,
    staleTime: 60_000,
  });
}

export function useLinkedDocuments(entityType: EntityType, entityId: string, enabled = true) {
  const wsId = useWsId();
  return useQuery({
    queryKey: docKeys.links(wsId, entityType, entityId),
    queryFn: () =>
      api.get<LinkedDocumentsResponse>(wsPath(wsId, 'documents', 'links'), { query: { entityType, entityId } }),
    enabled: Boolean(wsId && entityId) && enabled,
  });
}

export function useLinkableRecords(entityType: EntityType | null) {
  const wsId = useWsId();
  return useQuery({
    queryKey: docKeys.linkable(wsId, entityType ?? ''),
    queryFn: () => api.get<LinkableRecordsResponse>(wsPath(wsId, 'documents', 'linkable'), { query: { entityType: entityType ?? '' } }),
    enabled: Boolean(wsId && entityType),
  });
}

/** Short-lived signed URL (FR-2.5) for showing an image/PDF inline. Refreshed before the 5-minute expiry. */
export function useSignedViewUrl(id: string, enabled: boolean) {
  const wsId = useWsId();
  return useQuery({
    queryKey: docKeys.view(wsId, id, 'inline'),
    queryFn: () =>
      api.get<DocumentDownloadResponse>(wsPath(wsId, 'documents', id, 'download'), { query: { disposition: 'inline' } }),
    enabled: Boolean(wsId && id) && enabled,
    staleTime: 3 * 60_000,
    gcTime: 4 * 60_000,
    refetchInterval: 4 * 60_000,
  });
}

export async function getSignedUrl(wsId: string, id: string, disposition: 'inline' | 'attachment') {
  return api.get<DocumentDownloadResponse>(wsPath(wsId, 'documents', id, 'download'), { query: { disposition } });
}

export async function findDuplicates(wsId: string, hash: string) {
  return api.get<DocumentDuplicatesResponse>(wsPath(wsId, 'documents', 'duplicates'), { query: { hash } });
}

/** Invalidate everything documents-related (lists, checklist, summary, links) after a write. */
export function useInvalidateDocuments() {
  const qc = useQueryClient();
  const wsId = useWsId();
  return () => qc.invalidateQueries({ queryKey: docKeys.all(wsId) });
}

export function useUpdateDocument(id: string) {
  const wsId = useWsId();
  const invalidate = useInvalidateDocuments();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateDocumentRequest) => api.patch<DocumentDetail>(wsPath(wsId, 'documents', id), body),
    onSuccess: (d) => {
      qc.setQueryData(docKeys.detail(wsId, id), d);
      void invalidate();
    },
  });
}

export function useDeleteDocument(id: string) {
  const wsId = useWsId();
  const invalidate = useInvalidateDocuments();
  return useMutation({
    mutationFn: () => api.delete<{ ok: true }>(wsPath(wsId, 'documents', id)),
    onSuccess: () => void invalidate(),
  });
}

export function useRestoreDocument() {
  const wsId = useWsId();
  const invalidate = useInvalidateDocuments();
  return useMutation({
    mutationFn: (id: string) => api.post<DocumentDetail>(wsPath(wsId, 'documents', id, 'restore')),
    onSuccess: () => void invalidate(),
  });
}

export function useRetryText(id: string) {
  const wsId = useWsId();
  const invalidate = useInvalidateDocuments();
  return useMutation({
    mutationFn: () => api.post<DocumentTextResponse>(wsPath(wsId, 'documents', id, 'text', 'retry')),
    onSuccess: () => void invalidate(),
  });
}

export function useLinkDocuments() {
  const wsId = useWsId();
  const invalidate = useInvalidateDocuments();
  return useMutation({
    mutationFn: (body: { entityType: EntityType; entityId: string; documentIds: string[] }) =>
      api.post<CreateDocumentLinksResponse>(wsPath(wsId, 'documents', 'links'), body),
    onSuccess: () => void invalidate(),
  });
}

export function useUnlinkDocument() {
  const wsId = useWsId();
  const invalidate = useInvalidateDocuments();
  return useMutation({
    mutationFn: (q: { entityType: EntityType; entityId: string; documentId: string }) =>
      api.delete<{ ok: true }>(wsPath(wsId, 'documents', 'links'), { query: q }),
    onSuccess: () => void invalidate(),
  });
}
